// APP-6-style MILSYM icons as Leaflet divIcons.
// Friendly = blue rectangle frame; hostile/enemy = red diamond; objective = ringed cross.
// Frames are stylised (not full APP-6) but read at a glance on the ops wall.
import L from 'leaflet';

const FRIENDLY_FRAME = '#4aa3ff';
const HOSTILE_FRAME = '#ff4d4f';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

export interface FriendlyOpts {
  color: string; // phase color fill
  callsign: string;
  heading?: number | null;
  sos?: boolean;
  ghost?: boolean;
  // Optional uniform scale multiplier for the whole symbol (box/font/echelon
  // dots + iconSize/iconAnchor). Default 1 keeps the live map pixel-identical —
  // the Simulation Center passes ~1.5 for large, legible platoons (docs/17).
  scale?: number;
  // Permanent label chip under the symbol: short callsign + phase sub-line
  // (F-3). `accent` tints the border/sub-line (e.g. orange while executing).
  chip?: { title: string; sub?: string; accent?: string };
}

// Friendly platoon: blue rectangle, phase-colored fill, echelon dots (platoon = •••),
// optional heading arrow, SOS ring, ghost (faded/dashed for stale).
export function friendlyPlatoonIcon(o: FriendlyOpts): L.DivIcon {
  const frame = o.ghost ? '#9ca3af' : FRIENDLY_FRAME;
  const opacity = o.ghost ? 0.5 : 1;
  // Uniform scale; default 1 → identical rendering for existing callers.
  const s = o.scale ?? 1;
  const px = (n: number): number => +(n * s).toFixed(2);
  const heading =
    o.heading != null && isFinite(o.heading)
      ? `<div style="position:absolute;left:50%;top:${px(-14)}px;transform:translateX(-50%) rotate(${o.heading}deg);transform-origin:50% ${px(26)}px;">
           <div style="width:0;height:0;border-left:${px(5)}px solid transparent;border-right:${px(5)}px solid transparent;border-bottom:${px(9)}px solid ${frame};"></div>
         </div>`
      : '';
  const sos = o.sos
    ? `<div style="position:absolute;inset:${px(-7)}px;border:${px(2)}px solid #ff2d55;border-radius:${px(8)}px;animation:blink 1s step-end infinite;"></div>`
    : '';
  const chipAccent = o.chip?.accent || '#374151';
  const chipSubColor = o.chip?.accent || '#9ca3af';
  const chip = o.chip
    ? `<div style="position:absolute;left:50%;top:100%;transform:translateX(-50%);margin-top:${px(3)}px;
         background:rgba(10,14,20,.92);border:${px(1)}px solid ${chipAccent};border-radius:${px(4)}px;padding:${px(2)}px ${px(5)}px;
         text-align:center;box-shadow:0 1px 4px rgba(0,0,0,.6);">
         <div style="font:700 ${px(9)}px/1.15 'Share Tech Mono',monospace;color:#fff;white-space:nowrap;">${escapeHtml(o.chip.title)}</div>
         ${o.chip.sub
           ? `<div style="font:600 ${px(8)}px/1.25 'Share Tech Mono',monospace;color:${chipSubColor};white-space:nowrap;letter-spacing:.5px;">${escapeHtml(o.chip.sub)}</div>`
           : ''}
       </div>`
    : '';
  const html = `
    <div style="position:relative;opacity:${opacity};">
      ${heading}
      ${sos}
      <div style="display:flex;justify-content:center;gap:${px(2)}px;margin-bottom:${px(1)}px;">
        <span style="width:${px(3)}px;height:${px(3)}px;border-radius:50%;background:${frame};"></span>
        <span style="width:${px(3)}px;height:${px(3)}px;border-radius:50%;background:${frame};"></span>
        <span style="width:${px(3)}px;height:${px(3)}px;border-radius:50%;background:${frame};"></span>
      </div>
      <div style="min-width:${px(34)}px;height:${px(22)}px;padding:0 ${px(4)}px;border:${px(2)}px solid ${frame};border-radius:${px(3)}px;
                  background:${o.color}cc;${o.ghost ? 'border-style:dashed;' : ''}
                  display:flex;align-items:center;justify-content:center;
                  box-shadow:0 1px 4px rgba(0,0,0,.5);">
        <span style="font:700 ${px(10)}px/1 'Share Tech Mono',monospace;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.8);white-space:nowrap;">
          ${escapeHtml(o.callsign)}
        </span>
      </div>
      ${chip}
    </div>`;
  return L.divIcon({ className: 'platoon-marker', html, iconSize: [px(40), px(34)], iconAnchor: [px(20), px(22)] });
}

export interface EnemyOpts {
  color: string;
  emoji?: string;
  label?: string;
  dim?: boolean;
}

// Enemy target: red diamond frame with the target-type glyph.
export function enemyTargetIcon(o: EnemyOpts): L.DivIcon {
  const opacity = o.dim ? 0.45 : 1;
  const label = o.label
    ? `<div style="position:absolute;left:50%;top:30px;transform:translateX(-50%);white-space:nowrap;
         font:700 9px/1 'Share Tech Mono',monospace;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.9);">${escapeHtml(o.label)}</div>`
    : '';
  const html = `
    <div style="position:relative;opacity:${opacity};width:28px;height:28px;">
      <div style="position:absolute;inset:0;transform:rotate(45deg);border:2px solid ${HOSTILE_FRAME};border-radius:3px;
                  background:${o.color}aa;box-shadow:0 1px 4px rgba(0,0,0,.5);${o.dim ? 'border-style:dashed;' : ''}"></div>
      <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:13px;">${o.emoji ?? '🎯'}</div>
      ${label}
    </div>`;
  return L.divIcon({ className: 'target-marker', html, iconSize: [28, 28], iconAnchor: [14, 14] });
}

// Objective / anchor (e.g. farmbase): ringed cross.
export function objectiveIcon(o: { color: string; label?: string }): L.DivIcon {
  const label = o.label
    ? `<div style="position:absolute;left:50%;top:24px;transform:translateX(-50%);white-space:nowrap;
         font:700 9px/1 'Share Tech Mono',monospace;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.9);">${escapeHtml(o.label)}</div>`
    : '';
  const html = `
    <div style="position:relative;width:22px;height:22px;">
      <div style="position:absolute;inset:0;border:2px solid ${o.color};border-radius:50%;"></div>
      <div style="position:absolute;left:50%;top:0;bottom:0;width:2px;background:${o.color};transform:translateX(-50%);"></div>
      <div style="position:absolute;top:50%;left:0;right:0;height:2px;background:${o.color};transform:translateY(-50%);"></div>
      ${label}
    </div>`;
  return L.divIcon({ className: 'objective-marker', html, iconSize: [22, 22], iconAnchor: [11, 11] });
}
